import React, { useEffect, useState } from 'react';
import { Transfer, Space, message, Spin, Form } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { BusinessUnit, getAssignedUsersForBusinessUnit, assignUsersToBusinessUnit } from '../../services/businessunitservice';
import { getUsers } from '../../services/userservice';
import { User } from '../../types/types';
import FormDrawer from '../common/formdrawer';
import CancelButton from '../common/cancelbutton';
import FormButton from '../common/formbutton';
import { useAuth } from '../../auth/authcontext';

interface BuUsersDrawerProps {
  open: boolean;
  businessUnit: BusinessUnit | null;
  onClose: () => void;
}

const BuUsersDrawer: React.FC<BuUsersDrawerProps> = ({
  open,
  businessUnit,
  onClose,
}) => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isReadOnly } = useAuth();
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && businessUnit) {
      loadData();
    }
  }, [open, businessUnit]);

  const loadData = async () => {
    setLoading(true);
    try {
      const usersRes = await getUsers();
      setAllUsers(usersRes.data);

      const assignedRes = await getAssignedUsersForBusinessUnit(businessUnit!.guid);
      setTargetKeys(assignedRes);
    } catch (error) {
      message.error("Failed to load users for assignment.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!businessUnit) return;
    setSaving(true);
    try {
      await assignUsersToBusinessUnit(businessUnit.guid, targetKeys);
      message.success("User assignments updated successfully.");
      onClose();
    } catch (error) {
      message.error("Failed to save user assignments.");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (newTargetKeys: React.Key[]) => {
    setTargetKeys(newTargetKeys as string[]);
  };

  const transferData = allUsers.map((user) => ({
    key: user.guid,
    title: `${user.firstName} ${user.lastName} (${user.email})`,
    description: user.email,
  }));

  const footer = (
    <div style={{ textAlign: "right" }}>
      <Space>
        <CancelButton onClick={onClose} />
        <FormButton
          title="Save"
          icon={<EditOutlined />}
          onClick={handleSave}
          loading={saving}
        />
      </Space>
    </div>
  );

  return (
    <FormDrawer
      title={`Assign Users to ${businessUnit?.name || 'Business Unit'}`}
      open={open}
      onClose={onClose}
      onSave={handleSave}
      form={form}
      footer={footer}
      width={700}
    >
      <Spin spinning={loading}>
        <Transfer
          dataSource={transferData}
          titles={['Available Users', 'Assigned Users']}
          targetKeys={targetKeys}
          onChange={handleChange}
          render={(item) => item.title}
          listStyle={{
            width: '45%',
            height: 'calc(100vh - 200px)',
            minHeight: 400
          }}
          showSearch
          disabled={isReadOnly}
        />
      </Spin>
    </FormDrawer>
  );
};

export default BuUsersDrawer;
